import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useCompany } from '../Company/CompanyContext';
import { findNextLevel10Meeting, findNextEventsByQuery, formatCalendarEvent } from '../../services/calendarService';

function CalendarTest() {
  const { userProfile } = useAuth();
  const { companyInfo } = useCompany();
  
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [level10Meeting, setLevel10Meeting] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [testQuery, setTestQuery] = useState('');
  const [queryResults, setQueryResults] = useState([]);
  
  // Check if Google Calendar is connected
  useEffect(() => {
    if (companyInfo) {
      setIsConnected(!!companyInfo.googleCalendar);
    } else {
      setIsConnected(false);
    }
  }, [companyInfo]);
  
  // Get Next Level 10 Meeting
  const handleTestLevel10 = async () => {
    if (!companyInfo || !companyInfo.id || !companyInfo.googleCalendar) {
      setError('Google Calendar is not connected');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const meeting = await findNextLevel10Meeting(companyInfo.id);
      
      if (meeting) {
        setLevel10Meeting(formatCalendarEvent(meeting));
      } else {
        setLevel10Meeting(null);
        setError('No Level 10 meetings found in your calendar');
      }
    } catch (err) {
      console.error('Error finding Level 10 meeting:', err);
      setError(`Failed to find Level 10 meeting: ${err.message}`);
      setLevel10Meeting(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Get Upcoming Events
  const handleTestUpcoming = async () => {
    if (!companyInfo || !companyInfo.id || !companyInfo.googleCalendar) {
      setError('Google Calendar is not connected');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const events = await findNextEventsByQuery(companyInfo.id, '', 5);
      
      if (events && events.length > 0) {
        setUpcomingEvents(events.map(formatCalendarEvent).filter(Boolean));
      } else {
        setUpcomingEvents([]);
        setError('No upcoming events found in your calendar');
      }
    } catch (err) {
      console.error('Error finding upcoming events:', err);
      setError(`Failed to find upcoming events: ${err.message}`);
      setUpcomingEvents([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Test Custom Query
  const handleTestQuery = async () => {
    if (!companyInfo || !companyInfo.id || !companyInfo.googleCalendar) {
      setError('Google Calendar is not connected');
      return;
    }
    
    if (!testQuery.trim()) {
      setError('Please enter a query');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const events = await findNextEventsByQuery(companyInfo.id, testQuery, 5);
      
      if (events && events.length > 0) {
        setQueryResults(events.map(formatCalendarEvent).filter(Boolean));
      } else {
        setQueryResults([]);
        setError(`No events matching "${testQuery}" found in your calendar`);
      }
    } catch (err) {
      console.error('Error searching events:', err);
      setError(`Failed to search events: ${err.message}`);
      setQueryResults([]);
    } finally {
      setLoading(false);
    }
  };
  
  if (!userProfile) {
    return (
      <div className="p-4">
        <h1 className="text-xl font-bold mb-4">Calendar Test</h1>
        <p className="text-red-600">You must be logged in to test calendar integration</p>
      </div>
    );
  }
  
  // Format an event for display
  const renderEvent = (event) => (
    <div key={event.id} className="bg-white p-3 rounded-md shadow mb-3 border-l-4 border-blue-500">
      <h3 className="font-bold text-lg">{event.summary}</h3>
      <p className="text-gray-600">{event.date}</p>
      <p className="text-gray-700">{event.formattedTimeRange}</p>
      {event.location && <p className="text-gray-700">Location: {event.location}</p>}
      {event.description && (
        <div className="mt-2 pt-2 border-t border-gray-200">
          <p className="text-sm text-gray-600">{event.description}</p>
        </div>
      )}
      <a href={event.htmlLink} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline text-sm inline-block mt-2">
        View in Google Calendar
      </a>
    </div>
  );
  
  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Google Calendar Integration Test</h1>
      
      <div className="bg-gray-100 p-4 rounded-lg mb-6">
        <h2 className="text-lg font-semibold mb-2">Connection Status:</h2>
        {isConnected ? (
          <div className="bg-green-100 text-green-800 px-3 py-2 rounded-md">
            ✅ Google Calendar is connected
          </div>
        ) : (
          <div className="bg-red-100 text-red-800 px-3 py-2 rounded-md">
            ❌ Google Calendar is not connected
          </div>
        )}
        
        {!isConnected && (
          <p className="mt-2 text-sm text-gray-600">
            Connect your Google Calendar in the Settings page to test this functionality.
          </p>
        )}
      </div>
      
      {isConnected && (
        <>
          {error && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-3 mb-4 rounded-r">
              {error}
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Level 10 Meeting Test */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-3">Find Next Level 10 Meeting</h2>
              <button 
                onClick={handleTestLevel10}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Test Level 10 Meeting'}
              </button>
              
              {level10Meeting && (
                <div className="mt-4">
                  <h3 className="font-medium text-gray-700 mb-2">Result:</h3>
                  {renderEvent(level10Meeting)}
                </div>
              )}
            </div>
            
            {/* Upcoming Events Test */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-3">Find Upcoming Events</h2>
              <button 
                onClick={handleTestUpcoming}
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Test Upcoming Events'}
              </button>
              
              {upcomingEvents.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-medium text-gray-700 mb-2">Results ({upcomingEvents.length}):</h3>
                  <div className="max-h-80 overflow-y-auto">
                    {upcomingEvents.map(renderEvent)}
                  </div>
                </div>
              )}
            </div>
            
            {/* Custom Query Test */}
            <div className="border border-gray-200 rounded-lg p-4 md:col-span-2">
              <h2 className="text-lg font-semibold mb-3">Search Events</h2>
              
              <div className="flex space-x-2 mb-4">
                <input
                  type="text"
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  placeholder="Enter search term (e.g., 'sales', 'meeting')"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md"
                />
                <button 
                  onClick={handleTestQuery}
                  disabled={loading || !testQuery.trim()}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md shadow-sm disabled:opacity-50"
                >
                  {loading ? 'Searching...' : 'Search'}
                </button>
              </div>
              
              {queryResults.length > 0 && (
                <div>
                  <h3 className="font-medium text-gray-700 mb-2">Results ({queryResults.length}):</h3>
                  <div className="max-h-96 overflow-y-auto">
                    {queryResults.map(renderEvent)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default CalendarTest; 